<script>
export default {
    inheritAttrs: false
}
</script>

<script setup>
import { Footer, LeftSidebar } from '@/Components';
import TheDesktopNavbar from '@/Components/TheDesktopNavbar.vue';
import TheMobileNavbar from '@/Components/TheMobileNavbar.vue';
import TheRightSidebar from '@/Components/TheRightSidebar.vue';
import { useFlashMessages } from '@/composables';
import ImpersonationBanner from '@/Components/ImpersonationBanner.vue';
import useLayout from '@/useLayout';
import { twMerge } from 'tailwind-merge';
import { computed, useAttrs } from 'vue';
import { usePage, Head } from '@inertiajs/vue3';

const props = defineProps({
    title: {
        type: String,
        required: false
    },
    // whether to show the left sidebar
    withLeftSidebar: {
        type: Boolean,
        required: false,
        default: true
    },
    withFooter: {
        type: Boolean,
        required: false,
        default: true
    },
    navbarBackground: {
        type: String,
        required: false,
    }
});

const layout = useLayout();
const attrs = useAttrs();
useFlashMessages();
const page = usePage();

const isImpersonating = computed(() => page.props.auth.isImpersonating);
const isRightSidebarOpen = computed(() => layout.isRightSidebarOpen.value);
const isLeftSidebarOpen = computed({
    get() {
        return props.withLeftSidebar && layout.isLeftSidebarOpen.value;
    },
    set(value) {
        layout.isLeftSidebarOpen.value = value;
    }
});
</script>

<template>
    <Head>
        <title>{{ title ? `Personeo | ${title}` : 'Personeo' }}</title>
    </Head>
    
    <div class="flex flex-col min-h-screen bg-gradient" :class="{ 'pt-10': isImpersonating }">
        <ImpersonationBanner />
        <div :class="twMerge('layout flex-grow', attrs.class)">
            <div class="navbar-area sticky lg:static top-0 z-10">
                <TheMobileNavbar class="lg:hidden" :background="navbarBackground">
                    <slot name="mobile-navbar-title"></slot>
                    <template #right-sidebar-trigger>
                        <slot name="right-sidebar-trigger"></slot>
                    </template>
                </TheMobileNavbar>
                <TheDesktopNavbar class="hidden lg:flex" :background="navbarBackground" />
            </div>
            <div 
                class="left-sidebar-area transition-all ease-in-out duration-300 overflow-hidden"
                :class="[isLeftSidebarOpen ? 'w-0 lg:w-[var(--sidebar-width)]' : 'w-0']"
                :style="{'--sidebar-width': '300px'}"
            >
                <LeftSidebar @update:isOpen="isLeftSidebarOpen = !isLeftSidebarOpen" 
                    class="h-dvh w-[var(--sidebar-width)] fixed transition-all ease-in-out duration-300 z-20"
                    :class="[isLeftSidebarOpen ? 'translate-x-0' : '-translate-x-full']"
                />
            </div>
            <main class="main py-8">
                <div class="max-w-screen-xl mx-auto px-4">
                    <slot></slot>
                </div>
            </main>
            <div 
                v-if="$slots.rightSidebar"
                class="right-sidebar-area transition-all ease-in-out duration-300 overflow-hidden"
                :class="[isRightSidebarOpen ? 'w-0 lg:w-[var(--sidebar-width)]' : 'w-0']"
                :style="{'--sidebar-width': '300px'}"
            >
                <TheRightSidebar class="h-dvh w-[var(--sidebar-width)] fixed right-0 transition-all ease-in-out duration-300 z-10"
                    :class="[isRightSidebarOpen ? 'translate-x-0' : 'translate-x-full']"
                >
                    <slot name="rightSidebar" />
                </TheRightSidebar>
            </div>
            <div v-if="withFooter" class="footer-area py-12">
                <Footer />
            </div>
        </div>
    </div>
</template>

<style scoped>
.layout {
    min-height: 100dvh;
    display: grid;
    grid-template-rows: auto 1fr auto;
    grid-template-columns: 0 1fr 0;
    overflow-x: hidden;
    width: 100%;
    max-width: 100vw;
    grid-template-areas:
        "left-sidebar navbar right-sidebar"
        "left-sidebar main right-sidebar"
        "left-sidebar footer right-sidebar";
}

@media (min-width: 1024px) {
    .layout {
        grid-template-columns: auto 1fr auto;
    }
}

.navbar-area {
    grid-area: navbar;
}

.left-sidebar-area {
    grid-area: left-sidebar;
}

.main {
    grid-area: main;
    max-width: 100vw;
    overflow-x: hidden;
}

.right-sidebar-area {
    grid-area: right-sidebar;
}

.footer-area {
    grid-area: footer;
}
</style>
